/**
 * Cliente HTTP para los endpoints de Comparativos Financieros.
 * Usa el mismo patrón que RemoteResultsRepository: apiRequest + token de Clerk.
 *
 * CORRECCIONES respecto a la versión anterior:
 *  1. Prefijo correcto: /api/results/comparativos/… (antes era /results/comparativos/…)
 *  2. Token de autenticación pasado en cada petición (antes: fetch plano, sin Bearer)
 *  3. Header X-Lcc-Application incluido vía apiRequest → setCurrentApplicationHeader
 */

import { apiRequest } from '../http';
import { getAuthToken } from '../../auth/tokenProvider';
import {
  YearComparisonSummaryDto,
  MonthComparisonSummaryDto,
  AccumulatedComparisonSummaryDto,
  ClientYearComparisonRowDto,
  TopVariationItemDto,
  SummaryYearRowDto,
} from './types.comparativos';

const BASE = '/api/results/comparativos';

export class ComparativosRepository {
  constructor(private readonly getToken: () => Promise<string | null> = getAuthToken) {}

  private async get<T>(path: string, params: Record<string, string | number | string[] | undefined> = {}): Promise<T> {
    const parts: string[] = [];
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        if (v.length > 0) parts.push(`${k}=${encodeURIComponent(v.join(','))}`);
      } else {
        parts.push(`${k}=${encodeURIComponent(String(v))}`);
      }
    }
    const qs = parts.length > 0 ? '?' + parts.join('&') : '';
    const token = await this.getToken();
    return apiRequest<T>(`${BASE}${path}${qs}`, { token });
  }

  /** Comparativo año completo anio1 vs anio2, desglosado por cuenta. */
  getYearComparison(params: {
    anio1: number;
    anio2: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<YearComparisonSummaryDto> {
    return this.get('/year', params as any);
  }

  /** Comparativo mes puntual (puede cruzar años). */
  getMonthComparison(params: {
    mes1: string;
    anio1: number;
    mes2: string;
    anio2: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<MonthComparisonSummaryDto> {
    return this.get('/month', params as any);
  }

  /** Acumulado enero→mesCorte para dos años. */
  getAccumulatedComparison(params: {
    mesCorte: string;
    anio1: number;
    anio2: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<AccumulatedComparisonSummaryDto> {
    return this.get('/accumulated', params as any);
  }

  /** Comparativo por cliente entre dos años. */
  getClientYearComparison(params: {
    anio1: number;
    anio2: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<ClientYearComparisonRowDto[]> {
    return this.get('/clients-year', params as any);
  }

  /** Top N cuentas o clientes con mayor variación (positiva o negativa). */
  getTopVariations(params: {
    mode: 'year' | 'month' | 'accumulated';
    anio1: number;
    anio2: number;
    mes1?: string;
    mes2?: string;
    mesCorte?: string;
    topN?: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<TopVariationItemDto[]> {
    return this.get('/top-variations', params as any);
  }

  /** Tabla Resumen con comparación de dos años. */
  getSummaryYearComparison(params: {
    anio1: number;
    anio2: number;
    tiposServicio?: string[];
    clientes?: string[];
  }): Promise<SummaryYearRowDto[]> {
    return this.get('/summary-year', params as any);
  }
}

/** Singleton (una instancia por sesión, token se obtiene en cada llamada). */
let _instance: ComparativosRepository | null = null;

export function getComparativosRepository(): ComparativosRepository {
  if (!_instance) _instance = new ComparativosRepository();
  return _instance;
}
